import React, { useState, useRef } from "react";
import { Skeleton } from "antd";
import PopoverToolbar from "../EbulletinPopoverToolbar";
import UploadCard from "../EbulletinUploadCard";
import UploadModal from "../modals/EbulletinUploadModal";

export default function ImageBlock({
  block,
  isEditing,
  updateBlockProperty,
  updateBlockStyle,
  bulletinId,
  sectionId,
}) {
  const resizableRef = useRef(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showImagePopover, setShowImagePopover] = useState(false);

  // Parse block content if it's JSON (for media blocks)
  const getImageData = () => {
    if (block.url) {
      // New format - direct properties
      return {
        url: block.url,
        alt: block.alt || "",
        caption: block.caption || "",
        style: block.style || {},
      };
    } else if (block.content) {
      try {
        // Try to parse as JSON first (serialized media data)
        const parsed = JSON.parse(block.content);
        if (parsed.url) {
          return parsed;
        }
        // Fallback: treat content as direct URL
        return {
          url: block.content,
          alt: "",
          caption: "",
          style: {},
        };
      } catch (e) {
        // Not JSON, treat as direct URL
        return {
          url: block.content,
          alt: "",
          caption: "",
          style: {},
        };
      }
    }
    return { url: "", alt: "", caption: "", style: {} };
  };

  // Get image data from the block
  const imageData = getImageData();
  const imageUrl = imageData.url;

  // If no image URL, show a placeholder or skeleton
  const initResize = (e, direction) => {
    const img = resizableRef.current;
    if (!img) return;

    e.preventDefault();
    const startX = e.clientX;
    const startWidth = img.offsetWidth;

    // Function to handle resizing
    const doDrag = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const newWidth = direction === "left" ? startWidth - dx : startWidth + dx;
      img.style.width = `${Math.max(100, newWidth)}px`;
    };

    // Add mousemove and mouseup listeners to handle resizing
    const stopDrag = () => {
      document.removeEventListener("mousemove", doDrag);
      document.removeEventListener("mouseup", stopDrag);
      // Persist the new width in the block's style
      if (img) {
        const newStyle = { ...imageData.style, width: img.style.width };
        updateBlockStyle(newStyle);
      }
    };

    document.addEventListener("mousemove", doDrag);
    document.addEventListener("mouseup", stopDrag);
  };

  // Handle image upload completion
  const handleUploadFinish = (uploadResult) => {
    try {
      let newImageUrl, fileName, fileSize, mimeType;

      if (typeof uploadResult === "string") {
        newImageUrl = uploadResult;
      } else {
        newImageUrl = uploadResult.dataUrl || uploadResult.url;
        fileName = uploadResult.fileName || uploadResult.name;
        fileSize = uploadResult.fileSize || uploadResult.size;
        mimeType = uploadResult.mimeType || uploadResult.type;
      }

      // Update all image properties
      updateBlockProperty("url", newImageUrl);
      updateBlockProperty("alt", imageData.alt);
      updateBlockProperty("caption", imageData.caption);

      // Store additional metadata if available
      if (fileName) updateBlockProperty("fileName", fileName);
      if (fileSize) updateBlockProperty("fileSize", fileSize);
      if (mimeType) updateBlockProperty("mimeType", mimeType);

      setShowUploadModal(false);
    } catch (error) {
      console.error("Image upload error:", error);
    }
  };

  // Handle alignment changes
  const handleAlignChange = (align) => {
    const newStyle = { ...imageData.style, textAlign: align };
    updateBlockStyle(newStyle);
  };

  // Handle alt text changes
  const handleAltTextChange = (altText) => {
    updateBlockProperty("alt", altText);
  };

  // Handle caption changes
  const handleCaptionChange = (caption) => {
    updateBlockProperty("caption", caption);
  };

  // Render the image element with optional caption and alignment
  const imageElement = (
    <div className="image-wrapper">
      <img
        ref={resizableRef}
        src={imageUrl}
        alt={imageData.alt || "uploaded content"}
        className="resizable-image"
        draggable={false}
        style={imageData.style || {}}
        onError={(e) => {
          console.error("Image failed to load:", imageUrl);
          e.target.style.display = "none";
        }}
      />
      {imageData.caption && (
        <div
          className="image-caption"
          style={{ textAlign: imageData.style?.textAlign || "left" }}
        >
          {imageData.caption}
        </div>
      )}
    </div>
  );

  return (
    <div
      className="block-card image"
      style={{ textAlign: imageData.style?.textAlign || "left" }}
    >
      <UploadModal
        type="image"
        visible={showUploadModal}
        setVisible={setShowUploadModal}
        onUploadFinish={handleUploadFinish}
        bulletinId={bulletinId}
        sectionId={sectionId}
      />

      {!imageUrl ? (
        isEditing ? (
          <UploadCard type="image" onClick={() => setShowUploadModal(true)} />
        ) : (
          <Skeleton.Image active className="image-upload-placeholder" />
        )
      ) : (
        <div className="resizable-container">
          {isEditing ? (
            <>
              <div
                className="resize-handle left"
                onMouseDown={(e) => initResize(e, "left")}
              />
              <PopoverToolbar
                showPopover={showImagePopover}
                onOpenChange={setShowImagePopover}
                trigger="click"
                showUploadControls={true}
                showAlignControls={true}
                showImageControls={true}
                currentAlign={imageData.style?.textAlign || "left"}
                currentAlt={imageData.alt || ""}
                currentCaption={imageData.caption || ""}
                onUploadClick={() => {
                  setShowUploadModal(true);
                  setShowImagePopover(false);
                }}
                onAlignChange={handleAlignChange}
                onAltTextChange={handleAltTextChange}
                onCaptionChange={handleCaptionChange}
              >
                {imageElement}
              </PopoverToolbar>
              <div
                className="resize-handle right"
                onMouseDown={(e) => initResize(e, "right")}
              />
            </>
          ) : (
            imageElement
          )}
        </div>
      )}
    </div>
  );
}
